import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import dayjs, { Dayjs } from "dayjs";
import * as holiday_jp from "@holiday-jp/holiday_jp";

import IconButton from "../../../../components/button/IconButton";
import HoverCard from "../../../../components/hover-card/HoverCard";
import ToolTip from "../../../../components/tooltip/ToolTip";
import type { TippyProps } from "@tippyjs/react";
import type {
  ProducerTask,
  TaskColumn,
} from "../../../../app/services/taskBoard";
import type { Milestone } from "../../../../app/services/projects";
import {
  collectFilledCells,
  extractDates,
  extractMentions,
  isCellComplete,
  isDateInRange,
  isTaskComplete,
  type FilledCell,
  type TaskCompletionFilter,
  type TaskDateRange,
} from "./taskBoardUtils";
import GanttTaskHoverCard from "./GanttTaskHoverCard";
import { useHorizontalWheelPan } from "../../../../hooks/useHorizontalWheelPan";

/** セル単位のマーカー（バー領域に並べる小ドット）。 */
interface CellMarker {
  /** React key 用の安定 ID（列 ID + 日付） */
  id: string;
  /** 列のソート順（columns の index）。バー分断時にセグメント並び順を決めるのに使う */
  columnIndex: number;
  /** その列が「並列」か。並列なら左の列とバーを接続、直列なら左で分断する */
  parallel: boolean;
  columnTitle: string;
  mentions: string[];
  date: Dayjs;
  /** そのセルが完了済み（チェックボックスが全て ON）か。淡色表示に使う。 */
  complete: boolean;
  /** ドラッグ時にどのタスクのどの列を書き換えるかを特定するためのメタ情報。 */
  projectId: string;
  /** タスクが属するボード（プロジェクトは複数ボードを持てる）。 */
  boardId: string;
  taskId: string;
  columnId: string;
}

interface GanttRow {
  key: string;
  task: ProducerTask;
  dates: Dayjs[]; // 昇順
  markers: CellMarker[];
  /** タスク全体が完了済みか（行ラベルの淡色 + 打ち消し線に使う）。 */
  isComplete: boolean;
  /** 中身のある列（行ラベルのホバーカードに並べる）。 */
  filledCells: FilledCell[];
}

/** 見出し行に描画する、日付確定済みのマイルストーン。 */
interface GanttMilestone {
  event: string;
  date: Dayjs;
  /** `GanttProjectBoard.milestones`（API の生配列）内の添字。
   *  ドラッグでの日付更新時にどの要素を書き換えるかの特定に使う */
  sourceIndex: number;
}

/** ドラッグ対象。セルのドット、または見出し行のマイルストーン（ひし形）。 */
type DragTarget =
  | { kind: "cell"; marker: CellMarker }
  | { kind: "milestone"; projectId: string; milestone: GanttMilestone };

interface GanttGroup {
  /** プロジェクト + ボードで一意なグループキー。 */
  key: string;
  projectId: string;
  boardId: string;
  projectName: string;
  bannerColor?: string;
  /** 全体スケジュール開始日（任意）。 */
  startDate?: Dayjs;
  /** 全体スケジュール終了日（任意）。 */
  endDate?: Dayjs;
  /** 日付確定済みのマイルストーン（昇順）。 */
  milestones: GanttMilestone[];
  rows: GanttRow[];
}

/**
 * 横断ガントチャート。複数プロジェクトのタスクをプロジェクト単位で
 * グループ化して縦に並べ、セル本文に書かれた日付の範囲を横軸にバーで描画する。
 *
 * - 完了/未完了の判定は表（TaskBoardSection）と同じ `isTaskComplete` 基準だが、
 *   ガントは既定が `completionFilter="all"` で完了タスクも隠さずに全て描く。
 *   完了したタスク（行ラベル）と完了したセル（ドット・バー）は淡色 + 打ち消し線で
 *   区別する。表と同じ排他フィルターが要る場合だけ "incomplete" / "complete" を渡す。
 * - 表示対象タスクに過去日付があるときは横軸を最古のタスク日付まで過去方向へ
 *   拡張し、ドットを実際の日付位置に描画する（本日位置への張り付きはしない）。
 *   左スクロールで過去日付を確認でき、既定表示は本日が右寄りに来る位置。
 *   過去日付が無ければ従来どおり左端 = 本日。
 * - 行ラベルはタスク名のみ。プロジェクト名はグループ見出し行で表示する。
 * - 各セル（列）に書かれた日付を「セルマーカー」としてバー領域へ配置し、
 *   ホバーで `ToolTip`（Tippy）に「列名@担当者」を表示する。
 * - 担当フィルター（@表示名）はマーカー単位で適用する。
 * - タスク名フィルター（部分一致・大文字小文字無視）は行（タスク）単位で適用する。
 * - プロジェクト見出し行には、その全体スケジュール（start_date〜end_date）を
 *   期間ラインで、マイルストーンをひし形マーカーで重ねて表示する。
 * - 横軸の土日・祝日（非営業日）列は背景 `#f0f0f0` で薄く塗る。
 * - ヘッダー左上のボタンで横方向に縮小できる（拡大は既定倍率まで）。
 *   `WEEK_VIEW_MAX_DAY_PX` 未満まで縮めると週単位表示になり、週ごとの
 *   集約ドットで大きな進行具合を見る（詳細は週表示系の定数と renderWeekRow）。
 *
 * 描画コストの都合上、横軸は「見えている範囲だけ」描く（`visibleDayRange`）:
 * - 日付ヘッダーの目盛りと非営業日の列帯は可視範囲 + オーバースキャンのみ生成する
 * - 非営業日・当日の列帯は行ごとではなく本体全体に 1 枚だけ敷く（`ColumnOverlay`）
 * トラック幅（`totalDays * dayPx`）は実寸のままなので、スクロールバーの長さや
 * バー・ドットの座標計算・ドラッグの日数換算は仮想化の影響を受けない。
 *
 * ドラッグでの日付変更は親（CrossProjectTaskBoard）へ通知し、永続化（mutation）は
 * 親側で行う。本コンポーネント自身は API を直接叩かない。
 * - セルのドット: `onMoveMarker`（セル本文の日付表記を置換）
 * - マイルストーンのひし形: `onMoveMilestone`（プロジェクトの milestones を更新）
 */

export interface GanttProjectBoard {
  projectId: string;
  /** ボード ID。1 プロジェクトが複数ボードを持つので行グループの単位になる。 */
  boardId: string;
  projectName: string;
  /** プロジェクトバナーのカラー番号（`banner-color${N}` クラスに対応） */
  bannerColor?: string;
  columns: TaskColumn[];
  tasks: ProducerTask[];
  /** 全体スケジュール開始日（ISO 文字列、一覧API由来）。 */
  startDate?: string | null;
  /** 全体スケジュール終了日（ISO 文字列、一覧API由来）。 */
  endDate?: string | null;
  /** マイルストーン（一覧API由来）。 */
  milestones?: Milestone[];
  /** 編集可能か。false（閲覧のみ）の場合はドラッグでの日付変更を不可にする。 */
  canEdit?: boolean;
}

interface GanttChartProps {
  boards: GanttProjectBoard[];
  /** 担当者フィルター（@表示名）。空なら絞り込みなし。 */
  selectedMentions: string[];
  /** タスク名フィルター（部分一致・大文字小文字無視）。空なら絞り込みなし。 */
  taskNameFilter?: string;
  /**
   * 完了タスクの扱い。既定 "all"（完了・未完了を分けずに全て描画し、
   * 完了ぶんは淡色 + 打ち消し線で区別する）。初期スクロール位置はどの値でも
   * 本日をビューポート中央に置く（切り替えるたびに中央へ戻す）。
   */
  completionFilter?: TaskCompletionFilter;
  /** 日付範囲フィルター（日付チップ由来）。範囲内の日付を持たないタスクは隠す */
  dateRange?: TaskDateRange | null;
  /**
   * ドットドラッグで日付を変更したときに呼ばれるコールバック。
   * セル本文中の `fromDate` 表記を `toDate` に書式維持で置換した content を上位で
   * 永続化する（`replaceCellDate` を使って書き換える想定）。
   */
  // eslint-disable-next-line no-unused-vars
  onMoveMarker?: (params: {
    projectId: string;
    boardId: string;
    taskId: string;
    columnId: string;
    fromDate: Dayjs;
    toDate: Dayjs;
  }) => void;
  /**
   * マイルストーン（ひし形）をドラッグで動かしたときに呼ばれるコールバック。
   * `milestoneIndex` は `GanttProjectBoard.milestones` 配列の添字。永続化
   * （プロジェクトの milestones 全置換 PATCH）は上位で行う。
   */
  // eslint-disable-next-line no-unused-vars
  onMoveMilestone?: (params: {
    projectId: string;
    milestoneIndex: number;
    fromDate: Dayjs;
    toDate: Dayjs;
  }) => void;
}

/** 1 日の横幅（px）の最大値 = 既定値。ズームはここから縮小方向のみ（拡大なし）。 */
const MAX_DAY_PX = 28;
/**
 * ズームの段階（px/日、降順）。zoom_out で次に小さい値へ、zoom_in で次に大きい
 * 値へ移動する。全体表示（fit）は段階に無い中間値も取りうる。
 */
const ZOOM_DAY_PX_LEVELS = [28, 20, 14, 10, 7, 5, 3];
/** 全体表示（fit）で許す最小の 1 日幅（px）。これ未満には縮めない。 */
const MIN_FIT_DAY_PX = 2;
/**
 * 1 日幅がこの値未満になったら「週単位表示」へ切り替える。
 * 週表示では日々の目盛り・非営業日帯をやめ、同じ週のマーカーを 1 つの
 * ドットへ集約して、大きな進行具合を読み取りやすくする。
 */
const WEEK_VIEW_MAX_DAY_PX = 14;
/** 週表示で使う集約ドットの直径（px）。 */
const WEEK_DOT_DIAMETER = 8;
/** 週表示のヘッダーで週ラベル（M/D）を出す最小の週幅（px）。狭い週は月ラベルのみ。 */
const WEEK_DATE_LABEL_MIN_PX = 28;
// 基準の行高。一覧性を優先して表（約 36px）より詰める。ドット（12px）と
// タスク名（bodytext-13）が収まる最小限 + 上下の余白。
// 同日スタックのドットが多い行は、この値を最小値として行単位で広げる。
const ROW_HEIGHT = 30;
const GROUP_HEADER_HEIGHT = 36;
/**
 * 同一プロジェクトの 2 枚目以降のボード区切りの高さ。プロジェクトの区切り
 * （1 枚目）はスケジュール線・マイルストーンも載るため従来の高さを保ち、
 * ボードの切れ目はラベルなしの「単なる線」（バナー色の細い帯）にする。
 */
const GROUP_HEADER_COMPACT_HEIGHT = 4;
const LABEL_WIDTH = 240;
const HEADER_HEIGHT = 36;
const MIN_DAYS = 14;
const MAX_DAYS = 365;
/** ドット直径（px）。バー高さ・スタック間隔の計算で参照する */
const DOT_DIAMETER = 12;
/** 同日重なり時の縦方向間隔（px） */
const STACK_GAP = 10;
/** 同日スタックが多い行を広げるときの上下余白（合計 px） */
const ROW_STACK_PADDING = 8;
/** クリックとドラッグを切り分ける移動量しきい値（px） */
const DRAG_THRESHOLD_PX = 4;
/** 横軸の仮想化で可視範囲の外側にも余分に描く日数（スクロール時のちらつき防止） */
const OVERSCAN_DAYS = 10;
/** ビューポート未測定（初回レンダー）時に仮で使う描画幅（px） */
const FALLBACK_VIEWPORT_PX = 1600;
/**
 * チャート上部でビューポートを消費する分の目安（px）。
 * グローバルヘッダー 64 + タスクボードの TopAppBar 48 + 表示切替 / 担当者
 * フィルター / 検索欄とその gap ≈ 130 + Root の上下 padding 28。
 * 日付ラベル行を sticky にするには縦スクロールする器が必要なので、この値を
 * 引いた高さでチャートを区切る（区切らないと ScrollX が伸び切り、
 * 「スクロールしないスクロールポート」になって sticky が効かない）。
 */
const CHART_TOP_CHROME_PX = 270;
/** チャートの最小表示高さ（px）。狭い画面で潰れないようにする。 */
const CHART_MIN_HEIGHT_PX = 320;

/**
 * ドット/マイルストーンのツールチップに渡す Popper 設定。
 * - strategy:"fixed": ScrollX(overflow) や縦長ページのスクロール祖先によるクリップ・
 *   位置ズレを回避する（Tippy FAQ の定番対処）。
 * - computeStyles.adaptive:false + gpuAcceleration:false: transform ベースの配置をやめ
 *   top/left 直接指定にし、スクロール時に縦へ大きくズレる症状を防ぐ。
 * ドット/マイルストーン（= Tippy インスタンス）が多数 + スクロール発生時に効く。
 */
const TOOLTIP_POPPER_OPTIONS: TippyProps["popperOptions"] = {
  strategy: "fixed",
  modifiers: [
    {
      name: "computeStyles",
      options: { adaptive: false, gpuAcceleration: false },
    },
  ],
};

const Root = styled.div`
  width: 100%;
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 12px;
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
  padding: 12px 16px 16px;
`;

/**
 * チャートのスクロール領域（縦横）。
 *
 * 高さをビューポート基準で区切ることで、日付ラベル行（`HeaderRow`）を
 * `position: sticky; top: 0` で固定できるようにしている。sticky は「直近の
 * スクロールポート」を基準にするため、器が伸び切っていると効かない。
 * なお `overflow-x: auto` を指定した時点で CSS 仕様により overflow-y も
 * `auto` に計算されるので、縦は高さを与えるだけでスクロールするようになる。
 *
 * svh はモバイルのアドレスバー開閉で高さが変動しない単位。未対応環境向けに
 * vh を先に書いてフォールバックさせる（`ProfileStatement` と同じ流儀）。
 */
const ScrollX = styled.div`
  width: 100%;
  overflow-x: auto;
  max-height: max(
    ${CHART_MIN_HEIGHT_PX}px,
    calc(100vh - ${CHART_TOP_CHROME_PX}px)
  );
  max-height: max(
    ${CHART_MIN_HEIGHT_PX}px,
    calc(100svh - ${CHART_TOP_CHROME_PX}px)
  );

  /* グローバル既定（soremo_style.scss）はスクロールバーを hover 時のみ表示（非ホバー時は
     透明）にしているが、チャートがビューポートを超えたとき縦横にスクロールできると
     分かるよう常時表示する。
     標準プロパティ（scrollbar-width / scrollbar-color）が auto 以外だと Chrome は
     ::-webkit-scrollbar 系を無視するため、グローバルの thin / transparent を
     auto で打ち消してから WebKit スタイルで常時表示のバーを描画する
     （&& はグローバルの *:hover 規則より詳細度を上げるため）。 */
  && {
    scrollbar-width: auto;
    scrollbar-color: auto;
  }
  &::-webkit-scrollbar {
    display: block;
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: var(--soremo-background);
  }
  &::-webkit-scrollbar-thumb {
    background: var(--soremo-border);
    border-radius: 6px;
  }
  /* 縦横のバーが交差する角。track と同色で塗って隙間を目立たせない。 */
  &::-webkit-scrollbar-corner {
    background: var(--soremo-background);
  }
`;

const Grid = styled.div<{ $width: number }>`
  display: flex;
  flex-direction: column;
  width: ${({ $width }) => $width}px;
`;

/**
 * ヘッダーを除いた本体（グループ見出し + タスク行）。
 * 非営業日・当日の列帯を敷く `ColumnOverlay` の位置基準になる。
 */
const BodyArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

/**
 * 非営業日（土日・祝日）と当日の列帯を、本体の全行を貫いて 1 枚だけ敷くレイヤー。
 * 行ごとに列 div を並べると「行数 × 日数」個の要素になって重くなるため集約する。
 * `z-index: 0` で最背面に置き、行（`RowTrack` = position: relative）のバー・ドットは
 * DOM 順で後に来るので自然に上へ描画される。
 */
const ColumnOverlay = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  z-index: 0;
  pointer-events: none;
`;

const RowFlex = styled.div`
  display: flex;
  align-items: stretch;
`;

/**
 * ヘッダー行（タスク列 + 日付ラベル）。縦スクロールしても日付が見えるよう
 * `sticky; top: 0` で固定する。z-index は本文の sticky ラベル列（4）や
 * プロジェクト見出し行（1）より上に置き、行が下をくぐるようにする。
 *
 * この行に z-index を与えるとスタッキングコンテキストになるため、中の
 * `HeaderLabelCell`（左固定）と `DateAxis` の前後関係はこの行の内部で決まる。
 */
const HeaderRow = styled(RowFlex)`
  position: sticky;
  top: 0;
  z-index: 6;
`;

/**
 * ヘッダーのタスク列セル（表の左上の角）。横方向はここで左端に固定し、縦方向は
 * 親の `HeaderRow` が上端に固定するので、結果として角に留まる。
 * 背景を白で塗るのは、背後を流れる日付目盛りを透かさないため（表形式のセルは
 * 背景透過禁止・#fff 統一というルールに合わせる）。
 */
const HeaderLabelCell = styled.div`
  position: sticky;
  left: 0;
  z-index: 5;
  width: ${LABEL_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-right: 1px solid var(--soremo-border);
  border-bottom: 1px solid var(--soremo-border);
  height: ${HEADER_HEIGHT}px;
  background: #fff;
`;

/**
 * 日付ラベルの軸。sticky で固定した状態で下の行が透けないよう、不透過の白で塗る
 * （通常日の `DayTick` は背景を持たないため、ここで塗らないと行が透ける）。
 */
const DateAxis = styled.div<{ $width: number }>`
  position: relative;
  width: ${({ $width }) => $width}px;
  height: ${HEADER_HEIGHT}px;
  border-bottom: 1px solid var(--soremo-border);
  background: #fff;
`;

const DayTick = styled.div.attrs({ className: "bodytext-11" })<{
  $left: number;
  $width: number;
  $isMonthHead: boolean;
  $isWeekHead: boolean;
  $isToday: boolean;
  $isNonBusinessDay: boolean;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  color: var(--soremo-light-gray);
  border-left: ${({ $isMonthHead, $isWeekHead }) =>
    $isMonthHead
      ? "1px solid var(--soremo-light-gray)"
      : $isWeekHead
        ? "1px dashed var(--soremo-border)"
        : "none"};
  ${({ $isToday, $isNonBusinessDay }) =>
    $isToday
      ? "background: rgba(0, 154, 206, 0.08);"
      : $isNonBusinessDay
        ? "background: #f0f0f0;"
        : ""}
  display: flex;
  flex-direction: column;
  align-items: center;
  /* 月表示の有無に関わらず日付を下端で揃えるため下寄せにする。 */
  justify-content: flex-end;
  padding-bottom: 3px;
  user-select: none;
  /* bodytext-11 の line-height: 200% だと 2 行（月+日）が 36px に収まらないため、
     自クラスを二重化して詰める。 */
  && {
    line-height: 1.1;
  }
`;

/** 土日または祝日（非営業日）か。グレー背景帯の対象判定に使う。 */
const isNonBusinessDay = (
  d: Dayjs,
  holidayMap: Map<string, string>,
): boolean => {
  const day = d.day();
  return day === 0 || day === 6 || holidayMap.has(d.format("YYYY-MM-DD"));
};

/**
 * 週表示のヘッダー目盛り（1 週 = 1 セル）。ラベルは週の開始日（月曜）の
 * 「M/D」。月初を含む週には月ラベルを添える。区切り線は日表示の週境界
 * （破線）・月境界（実線）と同じ流儀。
 */
const WeekTick = styled.div.attrs({ className: "bodytext-11" })<{
  $left: number;
  $width: number;
  $isMonthHead: boolean;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  color: var(--soremo-light-gray);
  border-left: ${({ $isMonthHead }) =>
    $isMonthHead
      ? "1px solid var(--soremo-light-gray)"
      : "1px dashed var(--soremo-border)"};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 0 0 3px 3px;
  overflow: hidden;
  white-space: nowrap;
  user-select: none;
  /* DayTick と同じく 2 行（月 + 日付）を 36px に収めるため行間を詰める。 */
  && {
    line-height: 1.1;
  }
`;

/**
 * 週表示の本体に引く週境界線。非営業日帯の代わりに、週・月の区切りだけを
 * 薄く示す（帯を 1 日幅で敷くと数 px の縞模様になって読めないため）。
 */
const WeekBoundaryLine = styled.div<{ $left: number; $isMonthHead: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}px;
  width: 0;
  border-left: ${({ $isMonthHead }) =>
    $isMonthHead
      ? "1px solid var(--soremo-light-gray)"
      : "1px dashed var(--soremo-border)"};
`;

/**
 * 非営業日（土日・祝日）列の背景帯（#f0f0f0）。列位置はヘッダーの `DayTick` と揃える。
 * `ColumnOverlay` の中に置くので `pointer-events` は親で無効化済み。
 */
const NonBusinessDayCol = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  background: #f0f0f0;
`;

/**
 * 当日列の背景帯。ヘッダーの日付セル（$isToday）と同じ色で、見出し行・タスク行を
 * 通して列全体をハイライトする。非営業日帯より後に描画して当日色を優先する。
 */
const TodayCol = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  background: rgba(0, 154, 206, 0.08);
  pointer-events: none;
`;

/**
 * プロジェクト見出し行（全幅）。バナーのグラデーションを 1 枚で敷くため、
 * `banner-color${N}` クラスはこの親要素にのみ当てる。左のラベル列は sticky
 * 固定のため白で塗り潰すので、バナーが見えるのは日付トラック側だけになる。
 * バナー未設定時は `--soremo-border` の単色。
 *
 * `ColumnOverlay`（非営業日帯）より前面に出すため positioned + z-index 1 にする。
 * 見出し行に灰色の列帯が透けると、バナーの色が濁って見えるため。
 */
const GroupHeaderRow = styled.div.attrs<{ $bannerColor?: string }>(
  ({ $bannerColor }) => ({
    className: $bannerColor ? `banner-color${$bannerColor}` : undefined,
  }),
)<{ $bannerColor?: string }>`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  ${({ $bannerColor }) =>
    $bannerColor ? "" : "background: var(--soremo-border);"}
`;

/**
 * プロジェクト見出しのラベルセル（sticky で左固定）。
 *
 * 背景は白で塗る。sticky で固定すると親行のバナーグラデーションが背後を流れて
 * いくため、透明のままではセル内の文字コントラストが位置によって変わってしまう。
 * バナー色はタイムライン側（`GroupHeaderTrack`）に残し、表形式のセルは背景透過
 * なし・#fff 統一というルールに従う（表ビューの `TaskBoardSection` も
 * `SectionBody` を白にしてバナーはヘッダー側だけに置いている）。
 *
 * 白背景では文字色を黒へ戻す必要がある。親の `.banner-colorN div { color: #fff }`
 * は詳細度 (0,1,1) で単一クラスに勝つため、`&&` で詳細度を上げて確実に黒にする。
 *
 * `z-index` はドラッグ中のマイルストーンマーカー（z-index: 3）より上に置く。
 * `GroupHeaderRow` が z-index: 1 でスタッキングコンテキストを作るため、両者は
 * この行の内部で比較される。
 */
const GroupHeaderLabelCell = styled.div<{ $compact?: boolean }>`
  position: sticky;
  left: 0;
  z-index: 4;
  width: ${LABEL_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  height: ${({ $compact }) =>
    $compact ? GROUP_HEADER_COMPACT_HEIGHT : GROUP_HEADER_HEIGHT}px;
  border-right: 1px solid var(--soremo-border);
  border-bottom: 1px solid var(--soremo-border);
  background: #fff;

  && {
    color: #000;
  }
`;

/**
 * プロジェクト見出しの日付トラック（右側）。全体スケジュール線・マイルストーン
 * マーカーを重ねて描画する。背景は親行のグラデーションに任せて透明。
 */
const GroupHeaderTrack = styled.div<{ $width: number; $compact?: boolean }>`
  position: relative;
  width: ${({ $width }) => $width}px;
  height: ${({ $compact }) =>
    $compact ? GROUP_HEADER_COMPACT_HEIGHT : GROUP_HEADER_HEIGHT}px;
  border-bottom: 1px solid var(--soremo-border);
`;

/**
 * 全体スケジュール（start_date〜end_date）の期間ライン。細い直線で、シンプルに
 * 「プロジェクト全体の期間」を示す。色は `currentColor`（= `GroupHeaderTrack` が
 * 継承するバナーの文字色。背景の濃淡に応じた白／黒、バナー未設定時は既定の
 * 文字色）で、どのバナー色の上でも読めるようにする。
 */
const ScheduleLine = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 50%;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  height: 2px;
  margin-top: -1px;
  background: currentColor;
  pointer-events: none;
`;

/**
 * マイルストーンマーカー（ひし形）。見出しトラック上の該当日に置き、
 * ホバーで `ToolTip` にイベント名を表示する。色は `--soremo-green`。
 * 編集可能なプロジェクトではドラッグで日付を変更できる（grab カーソル）。
 */
const MilestoneMarker = styled.button.attrs({ type: "button" })<{
  $left: number;
  $editable?: boolean;
  $dragging?: boolean;
}>`
  position: absolute;
  top: 50%;
  left: ${({ $left }) => $left}px;
  width: ${DOT_DIAMETER}px;
  height: ${DOT_DIAMETER}px;
  margin-top: -${DOT_DIAMETER / 2}px;
  margin-left: -${DOT_DIAMETER / 2}px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: ${({ $editable, $dragging }) =>
    !$editable ? "default" : $dragging ? "grabbing" : "grab"};
  z-index: ${({ $dragging }) => ($dragging ? 3 : 2)};
  touch-action: none;

  /* ひし形の回転は疑似要素に閉じ込める。ボタン本体（Tippy/Popper の参照要素）を
     rotate すると getBoundingClientRect が対角線分だけ大きい矩形になり、Popper が
     「拡大されている」と誤検出して座標を補正し、ツールチップがドットから大きく
     離れて表示されてしまうため。 */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: ${DOT_DIAMETER}px;
    height: ${DOT_DIAMETER}px;
    box-sizing: border-box;
    border: 2px solid #fff;
    background: var(--soremo-green);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transform: rotate(45deg)
      ${({ $dragging }) => ($dragging ? " scale(1.3)" : "")};
    transition: transform 0.1s ease;
  }

  &:hover::before {
    transform: rotate(45deg) scale(1.3);
  }
`;

/**
 * プロジェクト見出し内のリンク（別タブでプロジェクトトップを開く）。表セクションの
 * プロジェクト名と同じ「heading-13」スタイルを使い、見た目とフォントを揃える。
 * 文字色はラベルセル（白背景 → 黒）から継承し、ラベルセル（240px 固定）に
 * 収まらない分は省略表示（ellipsis）する。
 */
const GroupHeaderLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: inherit;
  text-decoration: none;
  min-width: 0;

  & h3 {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/**
 * タスク名のセル（sticky で左固定）。横スクロールしてもタスク名が見えるように
 * 左端へ留める。背景は不透過の白。
 *
 * `RowTrack` は z-index: auto でスタッキングコンテキストを作らないため、中の
 * セルマーカー（通常 z-index: 2 / ドラッグ中 3）とこのセルは同じ文脈で比較され
 * る。ドラッグ中のドットにも隠れないよう 4 を与える。
 */
const TaskLabelCell = styled.div<{ $height: number }>`
  position: sticky;
  left: 0;
  z-index: 4;
  width: ${LABEL_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  border-right: 1px solid var(--soremo-border);
  border-bottom: 1px solid var(--soremo-border);
  height: ${({ $height }) => $height}px;
  background: #fff;
  overflow: hidden;
`;

/** 完了タスクは打ち消し線 + 淡色にして、未完了の行と一目で区別できるようにする。 */
const TaskTitle = styled.span<{ $completed: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ $completed }) =>
    $completed
      ? `text-decoration: line-through; color: var(--soremo-light-gray);`
      : ""}
`;

const RowTrack = styled.div<{ $width: number; $height: number }>`
  position: relative;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  border-bottom: 1px solid var(--soremo-border);
`;

/**
 * セル間の連続区間を示す「つなぎバー」。ドットを完全に包む太さ + フル角丸（pill）で
 * 区間の連続性を強調する。通常区間は `--soremo-light-gray`（非営業日帯の #f0f0f0 と
 * 被らないように）、期限超過区間は `--soremo-gray` で差別化し、直列列の境目では
 * 別セグメントとして描画され途切れる。完了済み区間は淡くして背景に沈める。
 * ドット（z-index:2）の真下に敷くため z-index は指定しない（自然順で背面）。
 */
const Bar = styled.div<{
  $left: number;
  $width: number;
  $overdue: boolean;
  $completed: boolean;
}>`
  position: absolute;
  top: 50%;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  height: 16px;
  margin-top: -8px;
  background: ${({ $overdue }) =>
    $overdue ? "var(--soremo-gray)" : "var(--soremo-light-gray)"};
  opacity: ${({ $completed }) => ($completed ? 0.35 : 1)};
  border-radius: 999px;
  pointer-events: none;
`;

/**
 * バー領域に置く「セルマーカー」。各セル（列）の日付位置に丸を 1 つ描画し、
 * ホバーで `ToolTip` に「列名@担当者」を表示する。
 * 同じ日付に複数のセルがあるときは `$top` で縦にスタックして全て見えるようにする。
 * 完了済みセルは淡いグレーにして、未完了のドットが埋もれないようにする。
 * ドラッグ中は `$dragging` でカーソルを `grabbing` に切り替え、transition を止めて追従感を出す。
 */
const MarkerDot = styled.button.attrs({ type: "button" })<{
  $left: number;
  $top: number;
  $size: number;
  $overdue: boolean;
  $dragging: boolean;
  $editable: boolean;
  $completed: boolean;
}>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  left: ${({ $left, $size }) => $left - $size / 2}px;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  padding: 0;
  border: 2px solid #fff;
  border-radius: 50%;
  background: ${({ $overdue, $completed }) =>
    $completed
      ? "var(--soremo-light-gray)"
      : $overdue
        ? "rgba(83, 86, 90, 0.95)"
        : "var(--soremo-blue)"};
  opacity: ${({ $completed }) => ($completed ? 0.6 : 1)};
  cursor: ${({ $editable, $dragging }) =>
    !$editable ? "default" : $dragging ? "grabbing" : "grab"};
  z-index: ${({ $dragging }) => ($dragging ? 3 : 2)};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: ${({ $dragging }) =>
    $dragging ? "none" : "transform 0.1s ease, box-shadow 0.1s ease"};
  touch-action: none;

  &:hover {
    transform: scale(1.4);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    opacity: 1;
  }
`;

const TodayLine = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}px;
  width: 2px;
  background: var(--soremo-blue);
  opacity: 0.6;
  pointer-events: none;
`;

const Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  color: var(--soremo-light-gray);
`;

/**
 * タスクの各セルから「列名 + 担当者 + 日付 + 並列/直列」のマーカーを生成する。
 * 完了済みセル（チェックボックスが1個以上あり全てON）は `includeCompleted=false`
 * のときガントに表示しない。表示する場合は `complete: true` を立てて淡色にする。
 */
function collectCellMarkers(
  projectId: string,
  boardId: string,
  task: ProducerTask,
  columns: TaskColumn[],
  base: Dayjs,
  includeCompleted: boolean,
): CellMarker[] {
  const result: CellMarker[] = [];
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    const cell = task.cells?.[col.id];
    if (!cell) continue;
    const content = cell.content ?? "";
    const complete = isCellComplete(content);
    // 完了フィルターで未完了のみを見ているときは、完了済みセルのドットもバーも出さない。
    if (!includeCompleted && complete) continue;
    const dates = extractDates(content, base);
    if (dates.length === 0) continue;
    const mentions = extractMentions(content);
    // 最初の列は「前列なし」なので並列扱い（分断しない）。
    const parallel = i === 0 ? true : col.parallel !== false;
    for (const date of dates) {
      result.push({
        // projectId + taskId + columnId + 日付で必ずユニークにする。
        // taskId/columnId が抜けると「別タスクの同列・同日のドット」と ID 衝突し、
        // ドラッグ時の `isDragTarget` 判定で他タスクのドットまで一緒に動いて見える。
        id: `${projectId}:${boardId}:${task.id}:${col.id}:${date.format("YYYY-MM-DD")}`,
        columnIndex: i,
        parallel,
        columnTitle: col.title,
        mentions,
        date,
        complete,
        projectId,
        boardId,
        taskId: task.id,
        columnId: col.id,
      });
    }
  }
  return result;
}

/**
 * マーカーを列順に並べたうえで「直列フラグ」で分断し、バーのセグメント配列に変換する。
 * 直列列（parallel=false）の到来時に新しいセグメントを開始するため、
 * 前段タスクの完了を待つ列以降は前段とつながらないバーになる。
 */
function buildBarSegments(markers: CellMarker[]): CellMarker[][] {
  if (markers.length === 0) return [];
  const sorted = [...markers].sort((a, b) => a.columnIndex - b.columnIndex);
  const segments: CellMarker[][] = [];
  let current: CellMarker[] = [];
  let prevColumnIndex = -1;
  for (const marker of sorted) {
    // 列をまたいだとき、その列が直列なら新セグメントへ。
    // 同じ列内（複数日付）はマーカーが続けて並ぶので分断しない。
    if (
      current.length > 0 &&
      marker.columnIndex !== prevColumnIndex &&
      !marker.parallel
    ) {
      segments.push(current);
      current = [];
    }
    current.push(marker);
    prevColumnIndex = marker.columnIndex;
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

/** マーカーの基本ラベル（「列名@担当者」形式、担当者なしなら列名のみ）。 */
function markerLabel(marker: CellMarker): string {
  return marker.mentions.length === 0
    ? marker.columnTitle
    : `${marker.columnTitle}${marker.mentions.map((m) => `@${m}`).join("")}`;
}

/** マーカーのツールチップ文字列（完了済みセルは「（完了）」を添える）。 */
function formatMarkerTooltip(marker: CellMarker): string {
  const base = markerLabel(marker);
  return marker.complete ? `${base}（完了）` : base;
}

/** ISO 文字列を日付（00:00）に変換する。空・無効なら undefined。 */
function parseScheduleDate(value?: string | null): Dayjs | undefined {
  if (!value) return undefined;
  const d = dayjs(value);
  return d.isValid() ? d.startOf("day") : undefined;
}

const GanttChart: React.FC<GanttChartProps> = ({
  boards,
  selectedMentions,
  taskNameFilter = "",
  completionFilter = "all",
  dateRange = null,
  onMoveMarker,
  onMoveMilestone,
}) => {
  const today = useMemo(() => dayjs().startOf("day"), []);

  // タスク名フィルターは部分一致・大文字小文字無視で照合する。
  const normalizedTaskName = taskNameFilter.trim().toLowerCase();

  // ── ドラッグ状態（セルのドット / 見出し行のマイルストーン共用） ──────────
  // 押下時に対象と開始座標を記録し、グローバルの pointermove で追従、
  // pointerup で「画面上で動いた日数」を整数化して onMoveMarker /
  // onMoveMilestone を呼ぶ。dragStartedRef で `DRAG_THRESHOLD_PX` 以下の
  // 移動はクリックとして扱い、誤発火を防ぐ。
  const [dragging, setDragging] = useState<{
    target: DragTarget;
    /** 押下時のクライアント X 座標 */
    startX: number;
  } | null>(null);
  const [dragDayOffset, setDragDayOffset] = useState(0);
  const dragStartedRef = useRef(false);

  // ── 横方向ズーム（縮小のみ） ────────────────────────────────────
  // 1 日の横幅（px）。既定 = 最大の MAX_DAY_PX で、ズームアウトで縮める。
  // 全体表示（fit）はズーム段階に無い中間値も取る。WEEK_VIEW_MAX_DAY_PX 未満
  // では「週単位表示」に切り替わる（目盛り・マーカー集約・ドラッグ不可）。
  const [dayPx, setDayPx] = useState<number>(MAX_DAY_PX);
  const isWeekView = dayPx < WEEK_VIEW_MAX_DAY_PX;

  /** ドット/ひし形共通のドラッグ開始処理（左クリックのみ受け付ける）。 */
  const beginDrag = (e: React.PointerEvent, target: DragTarget) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setDragging({ target, startX: e.clientX });
    setDragDayOffset(0);
    dragStartedRef.current = false;
  };

  // プロジェクトごとにグループ化。
  // - 行の出し分けは completionFilter に従う。既定 "all" は完了・未完了を
  //   まとめて表示し、完了ぶんは淡色 + 打ち消し線で区別する。
  // - 担当者フィルタが選択されている場合は「マーカー（セル）単位」で絞り込み、
  //   該当セルが0件になったタスクは行ごと非表示にする
  // - タスク名フィルタは行（タスク）単位で部分一致・大文字小文字無視で絞り込む
  // - 日付が一切無いタスクはガントに表示しない
  const groups = useMemo<GanttGroup[]>(() => {
    const includeCompleted = completionFilter !== "incomplete";
    const result: GanttGroup[] = [];
    for (const board of boards) {
      const rows: GanttRow[] = [];
      for (const task of board.tasks) {
        const isComplete = isTaskComplete(task, board.columns);
        if (completionFilter === "incomplete" && isComplete) continue;
        if (completionFilter === "complete" && !isComplete) continue;
        if (
          normalizedTaskName &&
          !(task.title ?? "").toLowerCase().includes(normalizedTaskName)
        ) {
          continue;
        }
        let markers = collectCellMarkers(
          board.projectId,
          board.boardId,
          task,
          board.columns,
          today,
          includeCompleted,
        );
        if (selectedMentions.length > 0) {
          markers = markers.filter((m) =>
            selectedMentions.some((s) => m.mentions.includes(s)),
          );
          if (markers.length === 0) continue;
        }
        // dates はフィルタ後のマーカーから再構成（バーの始点/終点を整合させる）
        const dates: Dayjs[] = [];
        for (const m of markers) {
          if (!dates.some((d) => d.isSame(m.date, "day"))) dates.push(m.date);
        }
        dates.sort((a, b) => a.valueOf() - b.valueOf());
        if (dates.length === 0) continue;
        // 日付チップ（先週 / 本日 / 明日 / 今週）で絞り込み中は、範囲内の日付を
        // 1 つも持たないタスクを行ごと隠す（表と同じ基準）。
        if (dateRange && !dates.some((d) => isDateInRange(d, dateRange))) {
          continue;
        }
        rows.push({
          key: `${board.projectId}:${board.boardId}:${task.id}`,
          task,
          dates,
          markers,
          isComplete,
          filledCells: collectFilledCells(task, board.columns),
        });
      }
      rows.sort((a, b) => {
        const av = a.dates[0].valueOf();
        const bv = b.dates[0].valueOf();
        if (av !== bv) return av - bv;
        return a.task.title.localeCompare(b.task.title);
      });

      // 全体スケジュール・マイルストーンを Dayjs に変換（見出し行で描画）。
      const startDate = parseScheduleDate(board.startDate);
      const endDate = parseScheduleDate(board.endDate);
      const milestones: GanttMilestone[] = (board.milestones ?? [])
        .map((m, sourceIndex) => {
          const date = parseScheduleDate(m.date);
          return date ? { event: m.event ?? "", date, sourceIndex } : null;
        })
        .filter((m): m is GanttMilestone => m !== null)
        .sort((a, b) => a.date.valueOf() - b.date.valueOf());

      const hasSchedule = !!startDate || !!endDate || milestones.length > 0;
      // 担当者フィルタ中・タスク名フィルタ中・完了のみ表示中・日付チップでの
      // 絞り込み中はタスクのあるプロジェクトのみ表示。フィルタなし時は
      // スケジュール/マイルストーンだけのプロジェクトも見出しとして表示する。
      const showGroup =
        rows.length > 0 ||
        (completionFilter !== "complete" &&
          selectedMentions.length === 0 &&
          !normalizedTaskName &&
          !dateRange &&
          hasSchedule);
      if (showGroup) {
        result.push({
          key: `${board.projectId}:${board.boardId}`,
          projectId: board.projectId,
          boardId: board.boardId,
          projectName: board.projectName,
          bannerColor: board.bannerColor,
          startDate,
          endDate,
          milestones,
          rows,
        });
      }
    }
    return result;
  }, [
    boards,
    selectedMentions,
    normalizedTaskName,
    completionFilter,
    dateRange,
    today,
  ]);

  // ボードごとの編集可否（ドラッグでの日付変更を許すか）。
  // 同じプロジェクトでもボード単位で閲覧のみになりうるため、プロジェクト
  // 単位で持つと最後のボードの可否で上書きされてしまう。キーは group.key と
  // 同じ `${projectId}:${boardId}`。
  const canEditByBoard = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const b of boards) {
      map.set(`${b.projectId}:${b.boardId}`, b.canEdit ?? true);
    }
    return map;
  }, [boards]);

  // 横軸の可視幅（スクロール位置と表示幅）。表示範囲の余白計算と横軸の仮想化に
  // 使う。値の更新はスクロール領域の effect（下方）で行う。
  const [viewport, setViewport] = useState({ scrollLeft: 0, clientWidth: 0 });

  // 本日を可視トラックの中央に置けるよう、本日の左右それぞれに確保する最低日数
  // （可視トラック幅の半分）。データの日付が本日の片側にしか無くても、端で
  // スクロールが止まって本日が中央からずれないようにする。既定倍率
  // （MAX_DAY_PX）基準で固定し、ズームで表示範囲（rangeStart）が動かないようにする。
  const minSideDays =
    viewport.clientWidth > 0
      ? Math.ceil(
          Math.max(0, viewport.clientWidth - LABEL_WIDTH) / 2 / MAX_DAY_PX,
        )
      : 0;

  // 横軸の未来方向の表示日数: 最遅日付 + 余白 / 最低 14 日（と中央寄せの余白）/
  // 最大 365 日
  const futureDays = useMemo(() => {
    let maxDayOffset = MIN_DAYS;
    for (const group of groups) {
      for (const row of group.rows) {
        const last = row.dates[row.dates.length - 1];
        const diff = last.diff(today, "day");
        if (diff > maxDayOffset) maxDayOffset = diff;
      }
      // 全体スケジュール終了日・マイルストーン日付も範囲に含める（バー/マーカーが
      // タスク日付より先にあっても見切れないようにする）。
      if (group.endDate) {
        const diff = group.endDate.diff(today, "day");
        if (diff > maxDayOffset) maxDayOffset = diff;
      }
      for (const m of group.milestones) {
        const diff = m.date.diff(today, "day");
        if (diff > maxDayOffset) maxDayOffset = diff;
      }
    }
    // 本日の列の右側に minSideDays 日ぶん並ぶよう、本日の列ぶん +1 する
    return Math.min(MAX_DAYS, Math.max(maxDayOffset + 7, minSideDays + 1));
  }, [groups, today, minSideDays]);

  // 横軸の過去方向の表示日数。過去日付のドットが本日位置に張り付いて
  // 当日列が密集しないよう、表示対象タスクやマイルストーンに過去日付が
  // あるときは最古の日付まで遡って表示範囲を広げる（+3 日の余白、上限 365 日）。
  // 左スクロールで過去が見える。過去日付が無くても中央寄せの余白（minSideDays）
  // ぶんは遡る。
  const pastDays = useMemo(() => {
    let minDayOffset = 0;
    for (const group of groups) {
      for (const row of group.rows) {
        const diff = row.dates[0].diff(today, "day");
        if (diff < minDayOffset) minDayOffset = diff;
      }
      for (const m of group.milestones) {
        const diff = m.date.diff(today, "day");
        if (diff < minDayOffset) minDayOffset = diff;
      }
    }
    const dataDays = minDayOffset >= 0 ? 0 : -minDayOffset + 3;
    return Math.min(MAX_DAYS, Math.max(dataDays, minSideDays));
  }, [groups, today, minSideDays]);

  // 表示範囲の起点と総日数
  const rangeStart = useMemo(
    () => today.subtract(pastDays, "day"),
    [pastDays, today],
  );
  const totalDays = pastDays + futureDays;

  // 表示範囲に含まれる日本の祝日。キーは "YYYY-MM-DD"、値は祝日名。
  // 土日と同じグレーで塗り、ヘッダーのツールチップに名称を出す。
  const holidayMap = useMemo(() => {
    const start = rangeStart.toDate();
    const end = rangeStart.add(totalDays, "day").toDate();
    const map = new Map<string, string>();
    for (const h of holiday_jp.between(start, end)) {
      map.set(dayjs(h.date).format("YYYY-MM-DD"), h.name);
    }
    return map;
  }, [rangeStart, totalDays]);

  const trackWidth = totalDays * dayPx;
  const totalWidth = LABEL_WIDTH + trackWidth;

  // スクロール領域の要素。state で保持する（グループ 0 件のときは描画されない
  // ため、ref だと「後から現れた要素」に対して effect が走らない）。
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  // トラックパッドの横スワイプでチャートを横パンできるようにする
  const wheelPanRef = useHorizontalWheelPan<HTMLDivElement>();
  // setScrollEl と wheelPanRef を 1 つの ref callback にまとめる
  // （インライン関数だと毎レンダーで listener が付け外しされるため memo 化）。
  const attachScrollEl = useCallback(
    (el: HTMLDivElement | null) => {
      setScrollEl(el);
      wheelPanRef(el);
    },
    [wheelPanRef],
  );

  // 自動センタリングとユーザー操作の切り分け。ユーザーが一度でも自分で
  // 横スクロールしたら、以後は表示範囲が変わっても勝手に動かさない
  // （ドットのドラッグで未来側が伸びるたびに視点が飛ぶのを防ぐ）。
  const hasUserScrolledRef = useRef(false);
  const autoScrollingRef = useRef(false);

  // 表示のたび（= ガントに切り替えたとき）の初期スクロール位置。完了フィルター
  // に関係なく、当日列をビューポート中央へ置く（左右スクロールで全体を見る）。
  // タスク列は sticky で固定されているため、実際にタイムラインが見えている幅は
  // `clientWidth - LABEL_WIDTH`。中央へ当日列を置くには、中央位置から
  // 「可視トラック幅の半分 + 固定列の幅」を引く（= (clientWidth + LABEL_WIDTH) / 2）。
  const applyInitialScroll = useCallback(() => {
    if (!scrollEl) return;
    const maxScrollLeft = Math.max(0, totalWidth - scrollEl.clientWidth);
    const todayCenterX = LABEL_WIDTH + pastDays * dayPx + dayPx / 2;
    const next = todayCenterX - (scrollEl.clientWidth + LABEL_WIDTH) / 2;
    autoScrollingRef.current = true;
    scrollEl.scrollLeft = Math.min(maxScrollLeft, Math.max(0, next));
    // scroll イベントは次フレームで届くので、そこまでは自動スクロール扱いにする
    requestAnimationFrame(() => {
      autoScrollingRef.current = false;
    });
  }, [scrollEl, pastDays, totalWidth, dayPx]);

  // 再マウント（表 → ガントの切り替え）と、完了フィルター・日付チップの
  // 切り替えのたびに自動スクロールを復活させ、本日を中央へ戻す。
  useEffect(() => {
    hasUserScrolledRef.current = false;
  }, [scrollEl, completionFilter, dateRange]);

  useEffect(() => {
    if (!scrollEl || hasUserScrolledRef.current) return;
    // マウント直後はレイアウト確定前で clientWidth が変わることがあるため、
    // 1 フレーム待ってから位置を計算する。
    const raf = requestAnimationFrame(applyInitialScroll);
    return () => cancelAnimationFrame(raf);
  }, [scrollEl, applyInitialScroll]);

  // ユーザーが自分でスクロールした後に表示範囲の起点（rangeStart）が動いた
  // とき（ウィンドウ幅の変更・過去日付の追加など）は、見ている日付が
  // 横へずれないよう、増減した日数ぶん scrollLeft を補正する。
  const prevPastDaysRef = useRef(pastDays);
  useLayoutEffect(() => {
    const prev = prevPastDaysRef.current;
    prevPastDaysRef.current = pastDays;
    if (!scrollEl || prev === pastDays || !hasUserScrolledRef.current) return;
    autoScrollingRef.current = true;
    scrollEl.scrollLeft += (pastDays - prev) * dayPx;
    requestAnimationFrame(() => {
      autoScrollingRef.current = false;
    });
  }, [pastDays, scrollEl, dayPx]);

  // ── 横軸の仮想化 ──────────────────────────────────────────────
  // 可視範囲を求めるためスクロール位置と表示幅（viewport）を追う。scroll は
  // 高頻度なので rAF で 1 フレーム 1 回に間引き、値が変わらなければ state を
  // 更新しない。
  useEffect(() => {
    if (!scrollEl) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      setViewport((prev) =>
        prev.scrollLeft === scrollEl.scrollLeft &&
        prev.clientWidth === scrollEl.clientWidth
          ? prev
          : {
              scrollLeft: scrollEl.scrollLeft,
              clientWidth: scrollEl.clientWidth,
            },
      );
    };
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(measure);
    };
    const handleScroll = () => {
      // 自動センタリング由来のスクロールはユーザー操作として数えない。
      if (!autoScrollingRef.current) hasUserScrolledRef.current = true;
      schedule();
    };
    measure();
    scrollEl.addEventListener("scroll", handleScroll, { passive: true });
    // リサイズは可視範囲の再計算だけ（ユーザーのスクロール扱いにはしない）。
    const observer = new ResizeObserver(schedule);
    observer.observe(scrollEl);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      scrollEl.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, [scrollEl]);

  // ── ズーム操作 ──────────────────────────────────────────────
  // 倍率変更の前に「可視トラック中央にある日付オフセット」を控えておき、
  // 新しい倍率でレンダリングされた直後（useLayoutEffect）に同じ日付が
  // 中央へ来るよう scrollLeft を補正する（ズームで視点が飛ばないように）。
  const pendingZoomCenterRef = useRef<number | null>(null);

  const applyZoom = useCallback(
    (nextDayPx: number) => {
      if (nextDayPx === dayPx) return;
      if (scrollEl) {
        const visibleTrack = Math.max(0, scrollEl.clientWidth - LABEL_WIDTH);
        pendingZoomCenterRef.current =
          (scrollEl.scrollLeft + visibleTrack / 2) / dayPx;
        // ズーム後の視点はユーザーが決めたものとして扱い、
        // 自動センタリング（applyInitialScroll）で上書きしない。
        hasUserScrolledRef.current = true;
      }
      setDayPx(nextDayPx);
    },
    [dayPx, scrollEl],
  );

  useLayoutEffect(() => {
    const centerDays = pendingZoomCenterRef.current;
    if (centerDays === null || !scrollEl) return;
    pendingZoomCenterRef.current = null;
    const visibleTrack = Math.max(0, scrollEl.clientWidth - LABEL_WIDTH);
    const next = centerDays * dayPx - visibleTrack / 2;
    const maxScrollLeft = Math.max(
      0,
      LABEL_WIDTH + totalDays * dayPx - scrollEl.clientWidth,
    );
    autoScrollingRef.current = true;
    scrollEl.scrollLeft = Math.min(maxScrollLeft, Math.max(0, next));
    requestAnimationFrame(() => {
      autoScrollingRef.current = false;
    });
  }, [dayPx, scrollEl, totalDays]);

  // 一段縮小 / 一段戻す。fit 由来の中間値から押しても最寄りの段階へスナップする。
  const zoomOutTarget =
    ZOOM_DAY_PX_LEVELS.find((v) => v < dayPx - 0.01) ?? null;
  const zoomInTarget =
    [...ZOOM_DAY_PX_LEVELS].reverse().find((v) => v > dayPx + 0.01) ?? null;

  /** 全体表示: 表示範囲の全日数が可視トラック幅に収まる倍率にする。 */
  const handleZoomFit = useCallback(() => {
    const width =
      (viewport.clientWidth || FALLBACK_VIEWPORT_PX) - LABEL_WIDTH;
    if (width <= 0 || totalDays <= 0) return;
    const fit = Math.min(
      MAX_DAY_PX,
      Math.max(MIN_FIT_DAY_PX, width / totalDays),
    );
    applyZoom(fit);
  }, [viewport.clientWidth, totalDays, applyZoom]);

  /** 実際に DOM を作る日付インデックスの範囲（[start, end)）。 */
  const visibleDayRange = useMemo(() => {
    // scrollLeft は Grid（ラベル列を含む）基準なので、トラック内座標へ直す。
    const trackLeft = Math.max(0, viewport.scrollLeft - LABEL_WIDTH);
    const width = viewport.clientWidth || FALLBACK_VIEWPORT_PX;
    const start = Math.max(
      0,
      Math.floor(trackLeft / dayPx) - OVERSCAN_DAYS,
    );
    const end = Math.min(
      totalDays,
      Math.ceil((trackLeft + width) / dayPx) + OVERSCAN_DAYS,
    );
    return { start, end: Math.max(start, end) };
  }, [viewport, totalDays, dayPx]);

  const visibleDayIndexes = useMemo(() => {
    const indexes: number[] = [];
    for (let i = visibleDayRange.start; i < visibleDayRange.end; i += 1) {
      indexes.push(i);
    }
    return indexes;
  }, [visibleDayRange]);

  // ── 週表示用の補助値 ──────────────────────────────────────────
  // 表示範囲の起点（rangeStart）を含む週の月曜日を、rangeStart からの日数
  // オフセット（0 以下）で持つ。週の区切りは実カレンダーの月曜に揃える。
  const firstWeekStartDay = useMemo(
    () => -((rangeStart.day() + 6) % 7),
    [rangeStart],
  );

  // 可視範囲と重なる週の一覧。週表示のみ。ヘッダー目盛りと週境界線で共用する
  // ため、月初週の判定（isMonthHead）と月ラベルもここでまとめて計算する。
  const visibleWeeks = useMemo(() => {
    if (!isWeekView) return [];
    const result: {
      key: string;
      /** rangeStart からの週開始日オフセット（日数。先頭週は負になりうる） */
      startDay: number;
      /** 週の開始日（月曜） */
      monday: Dayjs;
      /** 月初を含む週（週の途中で月が替わる場合も含む）か */
      isMonthHead: boolean;
      /** 月初週に添える「M月」。それ以外は null */
      monthLabel: string | null;
    }[] = [];
    const first = Math.floor(
      (visibleDayRange.start - firstWeekStartDay) / 7,
    );
    const last = Math.ceil((visibleDayRange.end - firstWeekStartDay) / 7);
    for (let w = first; w < last; w += 1) {
      const startDay = firstWeekStartDay + w * 7;
      if (startDay >= totalDays) break;
      if (startDay + 7 <= 0) continue;
      const monday = rangeStart.add(startDay, "day");
      const sunday = monday.add(6, "day");
      const isMonthHead =
        monday.date() === 1 || monday.month() !== sunday.month();
      const monthLabel = isMonthHead
        ? (monday.date() === 1 ? monday : sunday.startOf("month")).format(
            "M月",
          )
        : null;
      result.push({
        key: monday.format("YYYY-MM-DD"),
        startDay,
        monday,
        isMonthHead,
        monthLabel,
      });
    }
    return result;
  }, [isWeekView, visibleDayRange, firstWeekStartDay, rangeStart, totalDays]);

  // グローバル pointer 監視。ドラッグ中だけ document に listener を貼り、
  // 終了時に確実に外して二重発火/メモリリークを防ぐ。
  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: PointerEvent) => {
      const dx = e.clientX - dragging.startX;
      if (!dragStartedRef.current && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      dragStartedRef.current = true;
      // 1 日 = dayPx として、整数日にスナップ
      setDragDayOffset(Math.round(dx / dayPx));
    };
    const handleUp = (e: PointerEvent) => {
      const dx = e.clientX - dragging.startX;
      const days = Math.round(dx / dayPx);
      const moved = dragStartedRef.current && days !== 0;
      if (moved) {
        // 表示日付範囲外への移動も許す（範囲は再計算される）。
        const { target } = dragging;
        if (target.kind === "cell" && onMoveMarker) {
          onMoveMarker({
            projectId: target.marker.projectId,
            boardId: target.marker.boardId,
            taskId: target.marker.taskId,
            columnId: target.marker.columnId,
            fromDate: target.marker.date,
            toDate: target.marker.date.add(days, "day"),
          });
        } else if (target.kind === "milestone" && onMoveMilestone) {
          onMoveMilestone({
            projectId: target.projectId,
            milestoneIndex: target.milestone.sourceIndex,
            fromDate: target.milestone.date,
            toDate: target.milestone.date.add(days, "day"),
          });
        }
      }
      setDragging(null);
      setDragDayOffset(0);
      dragStartedRef.current = false;
    };
    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleUp);
    document.addEventListener("pointercancel", handleUp);
    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
      document.removeEventListener("pointercancel", handleUp);
    };
  }, [dragging, dayPx, onMoveMarker, onMoveMilestone]);

  if (groups.length === 0) {
    return (
      <Root>
        <Empty className="bodytext-13">
          {normalizedTaskName
            ? "タスク名に一致する予定がありません"
            : "表示できる予定がありません"}
        </Empty>
      </Root>
    );
  }

  // 表示範囲内に収めた日付オフセット（0〜totalDays-1、起点は rangeStart）。
  const clampOffset = (date: Dayjs) =>
    Math.max(0, Math.min(totalDays - 1, date.diff(rangeStart, "day")));

  // 列オフセット（日数）→ その列の中心 X 座標（px）。ドット・マイルストーン・
  // スケジュール線・バーの位置はすべてこの「列の中心」に揃える。
  const offsetToCenterX = (offset: number) => offset * dayPx + dayPx / 2;

  // 日付 → その日付が属する週（月曜始まり）の開始日オフセット（日数）。
  const weekStartDayOf = (date: Dayjs) => {
    const offset = clampOffset(date);
    return (
      firstWeekStartDay +
      Math.floor((offset - firstWeekStartDay) / 7) * 7
    );
  };

  // 日付 → その日付が属する週カラムの中心 X 座標（px）。週表示の集約ドットと
  // バーの端点に使う。表示範囲からはみ出す端の週は、見えている分の中心にする。
  const weekCenterX = (date: Dayjs) => {
    const startDay = weekStartDayOf(date);
    const spanStart = Math.max(0, startDay);
    const spanEnd = Math.min(totalDays, startDay + 7);
    return ((spanStart + spanEnd) / 2) * dayPx;
  };

  // 当日列の背景帯。非営業日帯の上・マーカー類の下に敷き、列全体をハイライトする
  // （rangeStart から pastDays 日目 = 本日）。
  const renderTodayCol = () => (
    <TodayCol $left={pastDays * dayPx} $width={dayPx} />
  );

  // 見出し行: 全体スケジュール（start_date〜end_date）の期間ライン。
  const renderScheduleLine = (group: GanttGroup) => {
    if (!group.startDate && !group.endDate) return null;
    const start = group.startDate ?? group.endDate ?? today;
    const end = group.endDate ?? group.startDate ?? today;
    const startOffset = clampOffset(start);
    const endOffset = clampOffset(end);
    if (endOffset < startOffset) return null;
    const left = offsetToCenterX(startOffset);
    const right = offsetToCenterX(endOffset);
    return (
      <ScheduleLine $left={left} $width={Math.max(dayPx, right - left)} />
    );
  };

  // 見出し行: マイルストーンマーカー（ひし形 + ホバーでイベント名）。
  // 編集可能なプロジェクトはドットと同様にドラッグで日付を変更できる。
  // マイルストーンはプロジェクト単位の情報なので、同じプロジェクトの別ボードに
  // 重複して並ばないよう、呼び出し側（CrossProjectTaskBoard）が先頭ボードの
  // グループにだけ渡してくる。
  const renderMilestones = (group: GanttGroup) =>
    group.milestones.map((m) => {
      const isDragTarget =
        dragging?.target.kind === "milestone" &&
        dragging.target.projectId === group.projectId &&
        dragging.target.milestone.sourceIndex === m.sourceIndex;
      // 閲覧のみ（view）のボードはドラッグでの日付変更を不可にする。
      // 週表示は 1 日が数 px しかなく正確に動かせないためドラッグ自体を無効化。
      const editable =
        !isWeekView &&
        !!onMoveMilestone &&
        (canEditByBoard.get(group.key) ?? true);
      // ドラッグ中はオフセット分だけ仮の日付に動かして表示する（確定は pointerup）。
      const displayDate = isDragTarget
        ? m.date.add(dragDayOffset, "day")
        : m.date;
      const left = offsetToCenterX(clampOffset(displayDate));
      const label = m.event || "マイルストーン";
      // ドラッグ中はツールチップが視界を塞ぐので強制非表示にする
      const tooltipVisible = dragging ? false : undefined;
      return (
        <ToolTip
          key={`ms-${group.key}-${m.sourceIndex}`}
          description={label}
          placement="top"
          visible={tooltipVisible}
          popperOptions={TOOLTIP_POPPER_OPTIONS}
        >
          <MilestoneMarker
            $left={left}
            $editable={editable}
            $dragging={isDragTarget}
            aria-label={label}
            onPointerDown={(e) => {
              if (!editable) return; // 閲覧のみは掴めない
              beginDrag(e, {
                kind: "milestone",
                projectId: group.projectId,
                milestone: m,
              });
            }}
          />
        </ToolTip>
      );
    });

  const renderMarker = (
    marker: CellMarker,
    stackIndex: number,
    stackCount: number,
    rowHeight: number,
  ) => {
    const isDragTarget =
      dragging?.target.kind === "cell" &&
      dragging.target.marker.id === marker.id;
    // 閲覧のみ（view）のボードはドラッグでの日付変更を不可にする。
    const editable =
      !!onMoveMarker &&
      (canEditByBoard.get(`${marker.projectId}:${marker.boardId}`) ?? true);
    // ドラッグ中はオフセット分だけ仮の日付に動かして表示する（確定は pointerup）。
    const displayDate = isDragTarget
      ? marker.date.add(dragDayOffset, "day")
      : marker.date;
    const leftPx = offsetToCenterX(clampOffset(displayDate));
    // 完了済みセルは日付が過ぎていても「遅れ」ではないので期限超過色にしない。
    const isOverdue = !marker.complete && displayDate.isBefore(today, "day");
    const tooltip = formatMarkerTooltip(marker);
    // 同日重なりのスタック位置: 中央 (stackCount-1)/2 を 0 として上下に振り分ける
    const centerY = rowHeight / 2 - DOT_DIAMETER / 2;
    const topPx =
      stackCount <= 1
        ? centerY
        : centerY + (stackIndex - (stackCount - 1) / 2) * STACK_GAP;
    // ドラッグ中はツールチップが視界を塞ぐので強制非表示にする
    const tooltipVisible = dragging ? false : undefined;
    return (
      <ToolTip
        key={marker.id}
        description={tooltip}
        placement="top"
        visible={tooltipVisible}
        popperOptions={TOOLTIP_POPPER_OPTIONS}
      >
        <MarkerDot
          $left={leftPx}
          $top={topPx}
          $size={DOT_DIAMETER}
          $overdue={isOverdue}
          $dragging={isDragTarget}
          $editable={editable}
          $completed={marker.complete}
          aria-label={tooltip}
          onPointerDown={(e) => {
            if (!editable) return; // 閲覧のみは掴めない
            beginDrag(e, { kind: "cell", marker });
          }}
        />
      </ToolTip>
    );
  };

  /**
   * 行ラベル（タスク名）セル。日表示・週表示で共用する。
   * 中身のある列があれば、ホバーでタスク名 + 各列の本文をタスクボードと同じ
   * 横並びの表で見せる（ドラッグ中は視界を塞ぐので出さない）。無ければ
   * タスク名が見切れたときだけツールチップで全文を出す。
   */
  const renderRowLabel = (row: GanttRow, rowHeight: number) => {
    const title = (
      <TaskTitle $completed={row.isComplete}>
        {row.task.title || "（無題）"}
      </TaskTitle>
    );
    return (
      <TaskLabelCell className="bodytext-13" $height={rowHeight}>
        {row.filledCells.length > 0 ? (
          <HoverCard
            content={
              <GanttTaskHoverCard
                task={row.task}
                cells={row.filledCells}
                isComplete={row.isComplete}
              />
            }
            // 列数ぶん横に広がるので、行ラベルの下から右方向へ開く
            placement="bottom-start"
            disabled={!!dragging}
            popperOptions={TOOLTIP_POPPER_OPTIONS}
          >
            {title}
          </HoverCard>
        ) : (
          <ToolTip description={row.task.title} truncatedOnly>
            {title}
          </ToolTip>
        )}
      </TaskLabelCell>
    );
  };

  /**
   * 週表示の 1 行。同じ週に属するマーカーを 1 つの集約ドットにまとめ、
   * バーの端点も週カラムの中心に揃える（ドットがバーの端に乗るように）。
   * 完了・期限超過の色分けは日表示と同じ基準で、集約ドットは
   * 「全マーカー完了なら淡色」「未完了の過去日付を含めば期限超過色」。
   * ドラッグでの日付変更は不可（1 日 = 数 px で正確に操作できないため）。
   */
  const renderWeekRow = (row: GanttRow) => {
    const segments = buildBarSegments(row.markers);
    // 週（月曜始まり）ごとにマーカーを集約する。キーは週開始日のオフセット。
    const markersByWeek = new Map<number, CellMarker[]>();
    for (const m of row.markers) {
      const k = weekStartDayOf(m.date);
      const arr = markersByWeek.get(k) ?? [];
      arr.push(m);
      markersByWeek.set(k, arr);
    }
    const halfDot = WEEK_DOT_DIAMETER / 2;
    return (
      <RowFlex key={row.key}>
        {renderRowLabel(row, ROW_HEIGHT)}
        <RowTrack $width={trackWidth} $height={ROW_HEIGHT}>
          {/* つなぎバー（直列列の境目で途切れる）。端点は週カラム中心。 */}
          {segments.map((seg, idx) => {
            if (seg.length < 2) return null;
            const dates = seg
              .map((m) => m.date)
              .sort((a, b) => a.valueOf() - b.valueOf());
            const first = dates[0];
            const last = dates[dates.length - 1];
            const startX = weekCenterX(first);
            const endX = weekCenterX(last);
            // 同じ週に収まるセグメントは集約ドットだけで十分
            if (endX <= startX) return null;
            const segmentComplete = seg.every((m) => m.complete);
            return (
              <Bar
                key={`seg-${idx}`}
                $left={startX - halfDot}
                $width={endX - startX + WEEK_DOT_DIAMETER}
                $overdue={!segmentComplete && last.isBefore(today, "day")}
                $completed={segmentComplete}
              />
            );
          })}
          {/* 週単位の集約ドット（ホバーで内訳 = 列名@担当者（M/D）を列挙） */}
          {[...markersByWeek.values()].map((ms) => {
            const sorted = [...ms].sort(
              (a, b) => a.date.valueOf() - b.date.valueOf(),
            );
            const complete = sorted.every((m) => m.complete);
            const overdue = sorted.some(
              (m) => !m.complete && m.date.isBefore(today, "day"),
            );
            const left = weekCenterX(sorted[0].date);
            const tooltip = sorted
              .map(
                (m) =>
                  `${markerLabel(m)}（${m.date.format("M/D")}${
                    m.complete ? "・完了" : ""
                  }）`,
              )
              .join(" / ");
            return (
              <ToolTip
                key={`wk-${sorted[0].id}`}
                description={tooltip}
                placement="top"
                popperOptions={TOOLTIP_POPPER_OPTIONS}
              >
                <MarkerDot
                  $left={left}
                  $top={ROW_HEIGHT / 2 - halfDot}
                  $size={WEEK_DOT_DIAMETER}
                  $overdue={overdue}
                  $dragging={false}
                  $editable={false}
                  $completed={complete}
                  aria-label={tooltip}
                />
              </ToolTip>
            );
          })}
        </RowTrack>
      </RowFlex>
    );
  };

  const renderRow = (row: GanttRow) => {
    if (isWeekView) return renderWeekRow(row);
    // 直列フラグでバーを分断したセグメント群
    const segments = buildBarSegments(row.markers);
    // 同じ日付に複数のセルマーカーがある場合に縦並びで全て見えるようにスタックする。
    const markersByDate = new Map<string, CellMarker[]>();
    for (const m of row.markers) {
      const k = m.date.format("YYYY-MM-DD");
      const arr = markersByDate.get(k) ?? [];
      arr.push(m);
      markersByDate.set(k, arr);
    }
    // 同日スタックの最大数に応じて行高を広げる（ドットが行からはみ出さないように）。
    const maxStack = Math.max(
      1,
      ...[...markersByDate.values()].map((ms) => ms.length),
    );
    const rowHeight = Math.max(
      ROW_HEIGHT,
      DOT_DIAMETER + (maxStack - 1) * STACK_GAP + ROW_STACK_PADDING,
    );
    return (
      <RowFlex key={row.key}>
        {renderRowLabel(row, rowHeight)}
        <RowTrack $width={trackWidth} $height={rowHeight}>
          {/* 非営業日・当日の列帯は BodyArea の ColumnOverlay が全行まとめて敷く */}
          {/* セグメント単位のつなぎバー（直列列の境目で途切れる）。
              単一日付セグメント（始点=終点）はドットだけで十分なのでバーは描画しない。 */}
          {segments.map((seg, idx) => {
            if (seg.length < 2) return null;
            const dates = seg
              .map((m) => m.date)
              .sort((a, b) => a.valueOf() - b.valueOf());
            const first = dates[0];
            const last = dates[dates.length - 1];
            if (first.isSame(last, "day")) return null;
            // セグメント内の全セルが完了していれば、そのバーも淡色にする。
            const segmentComplete = seg.every((m) => m.complete);
            // 両端のドットを完全に包み込むよう、中心 ± (DOT_DIAMETER/2) までバーを広げる
            const halfDot = DOT_DIAMETER / 2;
            const startPx = offsetToCenterX(clampOffset(first)) - halfDot;
            const endPx = offsetToCenterX(clampOffset(last)) + halfDot;
            return (
              <Bar
                key={`seg-${idx}`}
                $left={startPx}
                $width={Math.max(DOT_DIAMETER, endPx - startPx)}
                $overdue={!segmentComplete && last.isBefore(today, "day")}
                $completed={segmentComplete}
              />
            );
          })}
          {/* セル単位のマーカー（同日重なりは縦スタック） */}
          {[...markersByDate.values()].flatMap((ms) =>
            ms.map((m, i) => renderMarker(m, i, ms.length, rowHeight)),
          )}
        </RowTrack>
      </RowFlex>
    );
  };

  return (
    <Root>
      <ScrollX ref={attachScrollEl}>
        <Grid $width={totalWidth}>
          {/* ヘッダー（タスク + 日付軸）。縦スクロールでも上端に留まる。
              目盛りは可視範囲のみ生成する */}
          <HeaderRow>
            <HeaderLabelCell>
              <span className="bodytext-13 u-text-light-gray">タスク</span>
              {/* 横方向ズーム（縮小のみ）。縮小していくと週表示に切り替わる。 */}
              <div
                className="u-row u-items-center u-gap4"
                style={{ marginLeft: "auto" }}
              >
                <IconButton
                  icon="zoom_out"
                  size={18}
                  title="縮小"
                  ariaLabel="横軸を縮小"
                  disabled={zoomOutTarget === null}
                  onClick={() => zoomOutTarget && applyZoom(zoomOutTarget)}
                />
                <IconButton
                  icon="zoom_in"
                  size={18}
                  title="拡大（元の表示まで）"
                  ariaLabel="横軸を拡大"
                  disabled={zoomInTarget === null}
                  onClick={() => zoomInTarget && applyZoom(zoomInTarget)}
                />
                <IconButton
                  icon="fit_screen"
                  size={18}
                  title="全体を表示"
                  ariaLabel="全体を表示"
                  onClick={handleZoomFit}
                />
              </div>
            </HeaderLabelCell>
            <DateAxis $width={trackWidth}>
              {isWeekView
                ? // 週表示: 1 週 = 1 目盛り。ラベルは週開始日（月曜）の M/D、
                  // 月初を含む週には月ラベルを添える。週幅が狭いときは
                  // 日付ラベルを省いて月ラベルだけにする。
                  visibleWeeks.map(
                    ({ key, startDay, monday, isMonthHead, monthLabel }) => {
                      const weekPx = dayPx * 7;
                      return (
                        <WeekTick
                          key={key}
                          $left={startDay * dayPx}
                          $width={weekPx}
                          $isMonthHead={isMonthHead}
                        >
                          {monthLabel && (
                            <span style={{ fontWeight: 600 }}>
                              {monthLabel}
                            </span>
                          )}
                          {weekPx >= WEEK_DATE_LABEL_MIN_PX && (
                            <span>{monday.format("M/D")}</span>
                          )}
                        </WeekTick>
                      );
                    },
                  )
                : visibleDayIndexes.map((i) => {
                    const d = rangeStart.add(i, "day");
                    const holidayName = holidayMap.get(
                      d.format("YYYY-MM-DD"),
                    );
                    const tick = (
                      <DayTick
                        $left={i * dayPx}
                        $width={dayPx}
                        $isMonthHead={d.date() === 1}
                        $isWeekHead={d.day() === 1}
                        $isToday={d.isSame(today, "day")}
                        $isNonBusinessDay={isNonBusinessDay(d, holidayMap)}
                      >
                        {d.date() === 1 && (
                          <span style={{ fontWeight: 600 }}>
                            {d.format("M月")}
                          </span>
                        )}
                        <span>{d.format("D")}</span>
                      </DayTick>
                    );
                    // 祝日以外は説明が無いので Tippy インスタンスを作らない
                    // （日数ぶんの Tippy はスクロール時のコストが大きい）。
                    return holidayName ? (
                      <ToolTip key={i} description={holidayName}>
                        {tick}
                      </ToolTip>
                    ) : (
                      <React.Fragment key={i}>{tick}</React.Fragment>
                    );
                  })}
              {/* 本日ライン（過去拡張が無ければ横軸の左端 = 本日） */}
              <TodayLine $left={pastDays * dayPx} />
            </DateAxis>
          </HeaderRow>

          <BodyArea>
            {/* 非営業日・当日の列帯（全行を貫く 1 枚。可視範囲のみ生成）。
                週表示では非営業日帯の代わりに週・月の区切り線だけを引く。 */}
            <ColumnOverlay $left={LABEL_WIDTH} $width={trackWidth}>
              {isWeekView
                ? visibleWeeks.map(({ key, startDay, isMonthHead }) => {
                    if (startDay <= 0) return null;
                    return (
                      <WeekBoundaryLine
                        key={`wb-${key}`}
                        $left={startDay * dayPx}
                        $isMonthHead={isMonthHead}
                      />
                    );
                  })
                : visibleDayIndexes.map((i) => {
                    const d = rangeStart.add(i, "day");
                    if (!isNonBusinessDay(d, holidayMap)) return null;
                    return (
                      <NonBusinessDayCol
                        key={`nb-${i}`}
                        $left={i * dayPx}
                        $width={dayPx}
                      />
                    );
                  })}
              {renderTodayCol()}
            </ColumnOverlay>

            {/* プロジェクト単位のグループ。同一プロジェクトの 2 枚目以降の
                ボードの切れ目は、ラベルを持たないバナー色の細い帯
                （= 単なる区切り線）にする。 */}
            {groups.map((group, index) => {
              const compact =
                index > 0 && groups[index - 1].projectId === group.projectId;
              return (
                <React.Fragment key={group.key}>
                  <GroupHeaderRow $bannerColor={group.bannerColor}>
                    <GroupHeaderLabelCell $compact={compact}>
                      {!compact && (
                        <GroupHeaderLink
                          to={`/${group.projectId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${group.projectName}を別タブで開く`}
                        >
                          <h3 className="heading-13 u-line-height-200">
                            {group.projectName}
                          </h3>
                        </GroupHeaderLink>
                      )}
                    </GroupHeaderLabelCell>
                    <GroupHeaderTrack $width={trackWidth} $compact={compact}>
                      {/* 当日列のハイライト（バナー背景の上に重ねる） */}
                      {renderTodayCol()}
                      {/* 全体スケジュール期間ライン・マイルストーン
                          （プロジェクト先頭のボードにだけ載る） */}
                      {renderScheduleLine(group)}
                      {renderMilestones(group)}
                    </GroupHeaderTrack>
                  </GroupHeaderRow>
                  {group.rows.map((row) => renderRow(row))}
                </React.Fragment>
              );
            })}
          </BodyArea>
        </Grid>
      </ScrollX>
    </Root>
  );
};

export default GanttChart;
